import { useEffect, useRef, useState, type CSSProperties } from 'react'
import { useTranslation } from 'react-i18next'
import { motion, useReducedMotion } from 'framer-motion'
import {
  EASE_OUT,
  SPRING_POP,
  easeOutCubic,
  prefersReducedMotion,
} from '../../shared/motion.ts'
import { useCountUp } from './useCountUp'
import { GameHeader } from './components/GameHeader'
import { BottomSheetDrawer, type SheetTab } from './components/BottomSheetDrawer'
import { BetDock } from './components/BetDock'
import brandMark from '../../shared/assets/round6-snowfold-atlas-mark-v1.png'
import diceCore from '../../shared/assets/round17-dice-sealed-vellum-v2-640.webp'
import diceBetAura from '../../shared/assets/round17-dice-bet-aura-v1-768.webp'
import diceWorld from '../../shared/assets/round6-dice-snowfold-stage-v1.webp'
import {
  fetchBalance,
  getInitData,
  loginDev,
  loginWithInitData,
  play,
  quote,
  takeTokenFromUrl,
  type DiceRound,
} from './api'
import { goLobby } from './lobby'

export default function App() {
  const { t, i18n } = useTranslation()
  useEffect(() => {
    document.title = t('title')
  }, [t, i18n.language])

  const [token, setToken] = useState<string | null>(() => takeTokenFromUrl())
  const [balance, setBalance] = useState(0)
  const [authLoading, setAuthLoading] = useState(true)
  const [authError, setAuthError] = useState<string | null>(null)
  const [bet, setBet] = useState(100)
  const [target, setTarget] = useState(50)
  const [oddsUnder, setOddsUnder] = useState(0)
  const [oddsOver, setOddsOver] = useState(0)
  const [choice, setChoice] = useState<'under' | 'over'>('under')
  const [last, setLast] = useState<DiceRound | null>(null)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [quoteStatus, setQuoteStatus] = useState<'loading' | 'ready' | 'unavailable'>(
    'loading',
  )
  const [quoteRevision, setQuoteRevision] = useState(0)
  const [balanceStale, setBalanceStale] = useState(false)
  const [balanceOutcomeUnknown, setBalanceOutcomeUnknown] = useState(false)
  const [balanceRefreshing, setBalanceRefreshing] = useState(false)
  const [authAttempt, setAuthAttempt] = useState(0)
  const [sheetOpen, setSheetOpen] = useState(false)
  const [showChips, setShowChips] = useState(false)

  const quoteRequestRef = useRef(0)
  const playInFlightRef = useRef(false)
  const balanceRefreshRef = useRef(false)

  const reduceMotion = useReducedMotion()
  const displayBalance = useCountUp(balance)
  const oddsUnderDisplay = useCountUp(oddsUnder)
  const oddsOverDisplay = useCountUp(oddsOver)
  const underPctDisplay = useCountUp(target)
  const overPctDisplay = useCountUp(100 - target)

  const [revealing, setRevealing] = useState(false)
  const [rollDisplay, setRollDisplay] = useState(0)

  useEffect(() => {
    if (!last) return
    if (prefersReducedMotion()) {
      setRevealing(false)
      setRollDisplay(last.roll)
      return
    }
    setRevealing(true)
    const from = Math.max(3, Math.min(97, target + (last.roll >= target ? -28 : 28)))
    const started = performance.now()
    let raf = 0
    const tick = (now: number) => {
      const p = Math.min(1, (now - started) / 720)
      const eased = easeOutCubic(p)
      const settleWave = Math.sin(p * Math.PI * 5) * (1 - p) * 7
      const value = from + (last.roll - from) * eased + settleWave
      setRollDisplay(Math.max(0, Math.min(100, value)))
      if (p < 1) {
        raf = requestAnimationFrame(tick)
      } else {
        setRollDisplay(last.roll)
        setRevealing(false)
      }
    }
    raf = requestAnimationFrame(tick)
    return () => cancelAnimationFrame(raf)
  }, [last, target])

  useEffect(() => {
    let cancelled = false
    void (async () => {
      try {
        if (token) {
          const { balance: bal } = await fetchBalance(token)
          if (!cancelled) {
            setBalance(bal)
            setAuthLoading(false)
          }
          return
        }
        const init = await getInitData()
        const res = init ? await loginWithInitData(init) : await loginDev()
        if (cancelled) return
        setToken(res.token)
        setBalance(res.user.balance)
        setAuthLoading(false)
      } catch {
        if (!cancelled) {
          setAuthError(t('authFailed'))
          setAuthLoading(false)
        }
      }
    })()
    return () => {
      cancelled = true
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authAttempt])

  useEffect(() => {
    if (!token) return
    const reqId = ++quoteRequestRef.current
    setQuoteStatus('loading')
    setOddsUnder(0)
    setOddsOver(0)
    let cancelled = false
    void (async () => {
      try {
        const [qUnder, qOver] = await Promise.all([
          quote(token, target, 'under'),
          quote(token, target, 'over'),
        ])
        if (cancelled || reqId !== quoteRequestRef.current) return
        setOddsUnder(qUnder.odds)
        setOddsOver(qOver.odds)
        setQuoteStatus('ready')
      } catch {
        if (!cancelled && reqId === quoteRequestRef.current) {
          setQuoteStatus('unavailable')
        }
      }
    })()
    return () => {
      cancelled = true
    }
  }, [quoteRevision, token, target])

  const refreshBal = async (tok: string) => {
    if (balanceRefreshRef.current) return false
    balanceRefreshRef.current = true
    setBalanceRefreshing(true)
    try {
      const { balance: bal } = await fetchBalance(tok)
      setBalance(bal)
      setBalanceStale(false)
      setBalanceOutcomeUnknown(false)
      return true
    } catch {
      setBalanceStale(true)
      return false
    } finally {
      balanceRefreshRef.current = false
      setBalanceRefreshing(false)
    }
  }

  const onPlay = async (pickedChoice?: 'under' | 'over') => {
    const activeChoice = pickedChoice ?? choice
    if (!token || playInFlightRef.current || busy) return
    playInFlightRef.current = true
    setBusy(true)
    setError(null)
    setBalanceOutcomeUnknown(false)
    setLast(null)
    try {
      const res = await play(token, bet, target, activeChoice)
      setLast(res.round)
      await refreshBal(token)
    } catch {
      setBalanceOutcomeUnknown(true)
      setBalanceStale(true)
      setError(null)
    } finally {
      playInFlightRef.current = false
      setBusy(false)
    }
  }

  if (authLoading) {
    return (
      <div className="center">
        <div className="spinner" />
        <p>{t('connecting')}</p>
      </div>
    )
  }
  if (authError || !token) {
    return (
      <div className="center">
        <p className="error">{authError ?? t('unauthorized')}</p>
        <div className="dice-auth-actions">
          <button
            type="button"
            className="btn-lobby"
            onClick={() => {
              setAuthError(null)
              setAuthLoading(true)
              setAuthAttempt((value) => value + 1)
            }}
          >
            {t('retry')}
          </button>
          <button type="button" className="btn-lobby" onClick={() => goLobby(token)}>
            {t('backLobby')}
          </button>
        </div>
      </div>
    )
  }

  const liveChoice = last ? last.choice : choice
  const thumbPct = revealing || busy ? rollDisplay : target
  const settled = !!last && !revealing
  const phase = busy ? 'is-rolling' : settled ? 'is-settled' : 'is-idle'
  const boardNum = revealing ? rollDisplay : last ? last.roll : target
  const winRate = choice === 'under' ? target : 100 - target
  const betIsValid = bet >= 10 && bet <= balance
  const canPlayUnder = !busy && !balanceRefreshing && !balanceStale && betIsValid && quoteStatus === 'ready' && oddsUnder > 0
  const canPlayOver = !busy && !balanceRefreshing && !balanceStale && betIsValid && quoteStatus === 'ready' && oddsOver > 0

  const drawerTabs: SheetTab[] = [
    {
      id: 'rules',
      label: t('rules'),
      content: (
        <div style={{ lineHeight: 1.6 }}>
          <h3 style={{ margin: '0 0 8px 0', fontSize: '1rem', color: '#fff' }}>{t('rulesTitle')}</h3>
          <p style={{ color: 'var(--text-secondary)' }}>{t('rulesBody')}</p>
        </div>
      ),
    },
  ]

  // Tier 1: Target and win rate status
  const parameterSlot = (
    <div className="bet-dock-segmented" style={{ justifyContent: 'space-between', padding: '6px 10px', fontSize: '0.8rem', color: 'var(--text-secondary)' }}>
      <span>{t('underWinRate', { pct: underPctDisplay.toFixed(0) })}</span>
      <span>{t('overWinRate', { pct: overPctDisplay.toFixed(0) })}</span>
    </div>
  )

  const QUICK = [10, 50, 100, 250, 500]

  // Tier 2: Stake input & Collapsible Quick chips
  const amountSlot = (
    <div className="bet-dock-tier-amount">
      <div className="bet-dock-amount-main-row">
        <div className="bet-dock-input-group">
          <label htmlFor="dice-bet">{t('bet')}</label>
          <input
            id="dice-bet"
            type="number"
            min={10}
            max={balance}
            value={bet}
            disabled={busy}
            onChange={(e) => setBet(Math.max(0, Number(e.target.value) || 0))}
          />
        </div>
        <div className="bet-dock-quick-multipliers">
          <button
            type="button"
            className="bet-dock-multiplier-btn"
            disabled={busy}
            onClick={() => setBet(Math.max(10, Math.floor(bet / 2)))}
          >
            1/2
          </button>
          <button
            type="button"
            className="bet-dock-multiplier-btn"
            disabled={busy}
            onClick={() => setBet(Math.max(10, Math.min(balance, bet * 2)))}
          >
            2X
          </button>
          <button
            type="button"
            className="bet-dock-multiplier-btn"
            disabled={busy}
            onClick={() => setBet(Math.max(10, balance))}
          >
            {t('max')}
          </button>
          <button
            type="button"
            className={`bet-dock-chip-toggle${showChips ? ' active' : ''}`}
            disabled={busy}
            onClick={() => setShowChips(!showChips)}
            title={t('quickChips')}
            aria-label={t('quickChips')}
            aria-expanded={showChips}
            aria-controls="dice-quick-chips"
          >
            {showChips ? '▲' : '⚡'}
          </button>
        </div>
      </div>
      {showChips && (
        <div className="bet-dock-collapsible-chips" id="dice-quick-chips">
          {QUICK.map((q) => (
            <button
              key={q}
              type="button"
              className="bet-dock-preset-chip"
              disabled={busy}
              onClick={() => {
                setBet(q)
                setShowChips(false)
              }}
            >
              {q}
            </button>
          ))}
        </div>
      )}
    </div>
  )

  // Tier 3: Primary Action CTA with direct 1-click Roll
  const directionCtas = (
    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, width: '100%' }}>
      <motion.button
        type="button"
        className={`bet-dock-cta-btn state-ready${last?.won && last.choice === 'under' ? ' state-win' : ''}`}
        whileTap={{ scale: 0.97 }}
        disabled={!canPlayUnder}
        aria-pressed={choice === 'under'}
        onClick={() => {
          setChoice('under')
          void onPlay('under')
        }}
      >
        <span>{t('under')} {target}</span>
        <small style={{ opacity: 0.85, fontSize: '0.85em', marginLeft: 4 }}>
          {quoteStatus === 'ready' ? `(${oddsUnderDisplay.toFixed(2)}x)` : t('quotePendingShort')}
        </small>
      </motion.button>
      <motion.button
        type="button"
        className={`bet-dock-cta-btn state-ready${last?.won && last.choice === 'over' ? ' state-win' : ''}`}
        whileTap={{ scale: 0.97 }}
        disabled={!canPlayOver}
        aria-pressed={choice === 'over'}
        onClick={() => {
          setChoice('over')
          void onPlay('over')
        }}
      >
        <span>{t('over')} {target}</span>
        <small style={{ opacity: 0.85, fontSize: '0.85em', marginLeft: 4 }}>
          {quoteStatus === 'ready' ? `(${oddsOverDisplay.toFixed(2)}x)` : t('quotePendingShort')}
        </small>
      </motion.button>
    </div>
  )

  const ctaSlot = balanceStale ? (
    <button
      type="button"
      className="bet-dock-cta-btn state-ready"
      disabled={busy || balanceRefreshing}
      onClick={() => void refreshBal(token)}
    >
      {balanceRefreshing ? t('refreshingBalance') : t('retryBalance')}
    </button>
  ) : quoteStatus === 'unavailable' ? (
    <button
      type="button"
      className="bet-dock-cta-btn state-ready"
      onClick={() => setQuoteRevision((value) => value + 1)}
    >
      {t('retryQuotes')}
    </button>
  ) : directionCtas

  const metaSlot = (
    <>
      <span>{t('balance', { amount: Math.round(balance) })}</span>
      {last && (
        <span style={{ color: last.won ? 'var(--color-win)' : 'var(--color-danger)' }}>
          {last.won
            ? t('winPayout', { amount: last.payout })
            : t('loseAmount', { amount: last.bet_amount })}
        </span>
      )}
    </>
  )

  return (
    <div className="app app-single-viewport" data-game="dice">
      <GameHeader
        title={t('title')}
        brandSrc={brandMark}
        lobbyLabel={t('backLobby')}
        onLobby={() => goLobby(token)}
        balance={displayBalance}
        balanceLabel={t('balance', { amount: Math.round(displayBalance) })}
        onOpenRules={() => setSheetOpen(true)}
      />

      <div className="game-stage-viewport">
        <section
          className={`dice-stage ${phase}`}
          style={{ width: '100%', height: '100%', position: 'relative' }}
          data-phase={busy ? 'rolling' : revealing ? 'result' : settled ? 'result' : 'idle'}
          aria-busy={busy || revealing}
        >
          <img className="dice-stage-bg" src={diceWorld} alt="" />
          <div className="dice-stage-row">
            <div className="dice-board">
              <span className="dice-board-kicker">
                {last ? t('result') : t('target')}
              </span>
              <strong className="dice-board-num">
                {last ? Number(boardNum).toFixed(2) : Math.round(boardNum)}
              </strong>
              {last ? (
                settled ? (
                  <motion.span
                    className={`dice-outcome${last.won ? ' is-win' : ' is-lose'}`}
                    initial={reduceMotion ? false : { opacity: 0, y: 8 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={reduceMotion ? { duration: 0 } : SPRING_POP}
                  >
                    {last.won
                      ? t('winPayout', { amount: last.payout.toLocaleString('en-US') })
                      : t('loseAmount', { amount: last.bet_amount.toLocaleString('en-US') })}
                  </motion.span>
                ) : (
                  <span className="dice-board-sub">{t('rolling')}</span>
                )
              ) : (
                <span className="dice-board-sub">{winRate.toFixed(2)}%</span>
              )}
            </div>
            <span className="dice-status-live" role="status" aria-live="polite">
              {busy
                ? t('rolling')
                : settled && last
                  ? t('resultAnnouncement', {
                      roll: last.roll.toFixed(2),
                      outcome: last.won
                        ? t('winPayout', { amount: last.payout.toLocaleString('en-US') })
                        : t('loseAmount', { amount: last.bet_amount.toLocaleString('en-US') }),
                    })
                  : ''}
            </span>
            <div className="dice-stage-art" aria-hidden>
              <img
                className={`dice-bet-aura${busy ? ' is-live' : ''}`}
                src={diceBetAura}
                alt=""
              />
              <motion.img
                className={`dice-core${busy ? ' is-rolling' : ''}`}
                src={diceCore}
                alt=""
                animate={
                  busy && !reduceMotion
                    ? {
                        rotate: [0, 45, 90, 135, 180, 225, 270, 315, 360],
                        y: [0, -3, 0, 3, 0, -3, 0, 3, 0],
                        scale: [1, 0.92, 1, 0.92, 1, 0.92, 1, 0.92, 1],
                      }
                    : { rotate: 0, y: 0, scale: 1 }
                }
                transition={
                  busy && !reduceMotion
                    ? { duration: 1.2, ease: 'linear', repeat: Infinity }
                    : { duration: 0.26, ease: EASE_OUT }
                }
              />
            </div>
          </div>

          <div
            className={`dice-orbit dice-orbit--${liveChoice}`}
            style={
              {
                ['--dice-target' as string]: `${target}%`,
                ['--dice-thumb' as string]: `${thumbPct}%`,
              } as CSSProperties
            }
          >
            <div className="dice-orbit-win" aria-hidden />
            <div className="dice-orbit-lose" aria-hidden />
            <div className="dice-orbit-ticks" aria-hidden>
              <i /><i /><i /><i /><i />
            </div>
            <div className="dice-orbit-line" aria-hidden />
            <div className={`dice-orbit-thumb${last ? (last.won ? ' is-win' : ' is-lose') : ''}`} />
            <input
              className="dice-orbit-input"
              type="range"
              min={2}
              max={98}
              value={target}
              disabled={busy}
              aria-label={t('target')}
              onChange={(e) => {
                setLast(null)
                setTarget(Number(e.target.value))
              }}
            />
            <div className="dice-orbit-meta">
              <span>0</span>
              <span>{t('targetMark', { value: target })}</span>
              <span>100</span>
            </div>
          </div>
        </section>
      </div>

      <BetDock
        className="dice-bet-dock"
        parameterSlot={parameterSlot}
        amountSlot={amountSlot}
        ctaSlot={ctaSlot}
        metaSlot={metaSlot}
        error={balanceStale ? t(balanceOutcomeUnknown ? 'balanceOutcomeUnknown' : 'balanceStale') : quoteStatus === 'unavailable' ? t('quoteUnavailable') : error}
      />

      <BottomSheetDrawer
        isOpen={sheetOpen}
        onClose={() => setSheetOpen(false)}
        tabs={drawerTabs}
        closeLabel={t('close')}
      />
    </div>
  )
}
